import React from 'react';

import { aiMessages } from '@atlaskit/editor-common/ai-messages/ai';
import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages/ai-config-items';
import {
	AI_ASK_ROVO_MENU_ITEM,
	AI_BLOCK_MENU_LARGE_TEXT_THRESHOLD,
	AI_IMPROVE_WRITING_MENU_ITEM,
	AI_POLISH_MENU_ITEM,
} from '@atlaskit/editor-common/block-menu/key';
import { AICommandIcon } from '@atlaskit/editor-toolbar/ai-command-icon';
import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import { RovoIcon } from '@atlaskit/logo/rovo/icon';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { EditorPluginAIProvider } from '../../types/types';
import { improveWriting } from '../prebuilt/config-items/prompts/enhance/enhance';
import { addPolish } from '../prebuilt/config-items/prompts/polish/polish';
import type { BlockMenuActionDef } from './types';

const SMALL_WORD_COUNT_THRESHOLD = 5;

export const getBlockMenuActions = ({
	editorPluginAIProvider,
}: {
	editorPluginAIProvider: EditorPluginAIProvider;
}): BlockMenuActionDef[] => {
	const actions: BlockMenuActionDef[] = [
		{
			key: AI_ASK_ROVO_MENU_ITEM.key,
			label: aiMessages.askRovoToolbarIconTitle,
			elemBefore: <RovoIcon label="" size="xxsmall" />,
			configItem: editorPluginAIProvider.baseGenerate,
			shouldShowInPrimary: () =>
				expValEquals('platform_editor_ai_blockmenu_integration', 'isEnabled', true) ||
				isExperimentEnabled('platform_editor_ai_blockmenu_integration_jira'),
		},
		{
			key: AI_IMPROVE_WRITING_MENU_ITEM.key,
			label: aiConfigItemMessages.improveWritingTitle,
			elemBefore: <AICommandIcon label="" size="small" />,
			configItem: improveWriting(),
			shouldShowInPrimary: ({ textLength, wordCount }) =>
				wordCount >= SMALL_WORD_COUNT_THRESHOLD &&
				textLength < AI_BLOCK_MENU_LARGE_TEXT_THRESHOLD &&
				(expValEquals('platform_editor_ai_blockmenu_integration', 'isEnabled', true) ||
					isExperimentEnabled('platform_editor_ai_blockmenu_integration_jira')),
		},
	];

	const disabledByProduct = new Set(editorPluginAIProvider.disableBlockMenuActions);

	const isPolishReplacedByRemix =
		expValEquals('platform_editor_remix_in_block_menu', 'isEnabled', true) &&
		(!isExperimentEnabled('platform_editor_remix_in_block_menu_v2') ||
			disabledByProduct.has(AI_POLISH_MENU_ITEM.key));

	if (!isPolishReplacedByRemix) {
		actions.push({
			key: AI_POLISH_MENU_ITEM.key,
			label: aiConfigItemMessages.improveFormattingTitle,
			elemBefore: <AiGenerativeCleanupIcon label="" size="small" />,
			configItem: addPolish(),
			shouldShowInPrimary: ({ textLength }) =>
				textLength >= AI_BLOCK_MENU_LARGE_TEXT_THRESHOLD &&
				(expValEquals('platform_editor_ai_blockmenu_integration', 'isEnabled', true) ||
					isExperimentEnabled('platform_editor_ai_blockmenu_integration_jira')),
		});
	}

	return actions.filter(
		(action) => action.key === AI_POLISH_MENU_ITEM.key || !disabledByProduct.has(action.key),
	);
};
